/**
 * Token + auth storage helpers.
 * Keys match economy-app / BO (`auth`, `auth-token`) so a minted staging JWT can be
 * injected by hand (`localStorage.setItem('auth-token', …)`) during agent validation.
 */

import type { AuthStorage, UserObject } from '../types';

const AUTH_KEY = 'auth';
const AUTH_TOKEN_KEY = 'auth-token';
const GOOGLE_USER_KEY = 'google_user';

export function getAuthFromStorage(): AuthStorage {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AuthStorage;
  } catch {
    return {};
  }
}

export function updateAuthInStorage(updates: Partial<AuthStorage>): void {
  const next = { ...getAuthFromStorage(), ...updates };
  localStorage.setItem(AUTH_KEY, JSON.stringify(next));
  if (next.token) {
    localStorage.setItem(AUTH_TOKEN_KEY, next.token);
  }
}

export function clearAuthFromStorage(): void {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(GOOGLE_USER_KEY);
}

export function getTokenFromStorage(): string | null {
  return getAuthFromStorage().token || localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setGoogleUserInStorage(user: UserObject): void {
  localStorage.setItem(GOOGLE_USER_KEY, JSON.stringify(user));
}

export function getGoogleUserFromStorage(): UserObject | null {
  try {
    const raw = localStorage.getItem(GOOGLE_USER_KEY);
    return raw ? (JSON.parse(raw) as UserObject) : null;
  } catch {
    localStorage.removeItem(GOOGLE_USER_KEY);
    return null;
  }
}

/** Map auth email + optional GIS profile into the SPA user object. */
export function userFromAuth(
  managerEmail: string,
  googleUser?: { email?: string; name?: string; picture?: string },
): UserObject {
  const email = googleUser?.email || managerEmail;
  return {
    user_id: `admin-${managerEmail}`,
    email,
    name: googleUser?.name || email.split('@')[0],
    picture: googleUser?.picture,
    is_admin: true,
  };
}
